<template>
	<b-container>
		<b-form @submit.prevent>
			<b-row class="mt-3">
				<b-col cols="12">
					<h1>
						{{ contract.Reference }} -
						<b-link
                            v-if="contract.Customer"
							:to="{
								name: 'customerupdate',
								params: { id: contract.Customer.Id },
							}"
							>{{ contract.Customer.Name }}</b-link
						>
					</h1>
				</b-col>
			</b-row>

			<b-row class="mt-3">
				<b-col>
					<b-link
						class="float-right ml-3"
						@click.prevent="deleteContract"
						title="Delete this service contract"
					>
						<b-icon
							font-scale="1.5"
							icon="trash"
							variant="danger"
						></b-icon>
					</b-link>
					<save-or-edit-buttons
						:editable="detailsEditable"
						:creating="creating"
						section="detailsEditable"
						@save="saveSection"
						@edit="editSection"
					></save-or-edit-buttons>
				</b-col>
			</b-row>

			<b-row class="mt-3" v-if="detailsEditable">
				<b-col cols="3">
					<b-form-group label="Set status" for="contractStatusId">
						<b-form-select
							id="contractStatusId"
							v-model="contract.ContractStatusId"
							value-field="Id"
							text-field="Name"
							:options="states"
						></b-form-select>
					</b-form-group>
				</b-col>
				<b-col cols="3">
					<b-form-group label="Frequency" for="frequency">
						<b-form-input
							id="frequency"
							placeholder="Frequency"
							v-model="contract.Frequency"
							maxlength="60"
							required
						/>
					</b-form-group>
				</b-col>
				<b-col cols="3">
					<b-form-group label="Cost Code" for="invoiceCode">
						<b-form-select
							id="invoiceCode"
							v-model="contract.InvoiceCodeId"
							value-field="Id"
							text-field="Code"
							:options="invoiceCodes"
						></b-form-select>
					</b-form-group>
				</b-col>
				<b-col cols="3">
					<b-form-group label="First Service" for="firstService">
						<b-form-datepicker
							id="firstService"
							v-model="contract.FirstService"
							required
						></b-form-datepicker>
					</b-form-group>
				</b-col>
			</b-row>
			<b-row class="mt-3" v-else>
				<b-col cols="12">
					<h2>
						{{ statusName }} |
						{{ contract.Frequency }} Months |
						{{ invoiceCodeName }} | {{ contract.FirstService }}
					</h2>
				</b-col>
			</b-row>

			<b-row class="mt-3">
				<b-col>
					<document-asset
						:asset="contract.ServiceContractFile"
						:relate="contract.Id"
						type="0"
						folder="ServiceContract"
					/>
				</b-col>
			</b-row>

			<b-tabs content-class="mt-3">
				<b-tab title="Clocks" active>
					<b-row>
						<b-col cols="10">
							<h4>Clocks</h4>
						</b-col>
						<b-col cols="2">
							<b-link
								class="float-right mb-3"
								v-b-modal.sharedModal
								@click.prevent="
									modalContent = 'clocks';
									modalTitle = 'Select Clocks';
								"
							>
								<b-icon
									font-scale="1.5"
									icon="plus-square"
								></b-icon>
							</b-link>
						</b-col>
					</b-row>

					<b-form-fieldset>
						<b-table-simple bordered>
							<b-thead head-variant="light">
								<tr>
									<th>Name</th>
									<th>Location</th>
									<th style="width: 12.5%"></th>
								</tr>
							</b-thead>

							<b-tbody>
								<tr
									v-for="(clock, index) in contract.Clocks"
									:key="index + 10"
								>
									<td>
										<b-link
											:to="{
												name: 'clockupdate',
												params: { id: clock.Id },
											}"
											>{{ clock.Name }}</b-link
										>
									</td>

									<td>{{ clock.Address.Town }}</td>

									<td class="text-right">
										<b-link
											@click.prevent="
												removeFromArray(
													'Clocks',
													index,
													true
												)
											"
										>
											<b-icon
												font-scale="1.5"
												icon="dash-square"
												variant="danger"
											></b-icon>
										</b-link>
									</td>
								</tr>
							</b-tbody>
						</b-table-simple>
					</b-form-fieldset>
				</b-tab>

				<b-tab title="Service history"> </b-tab>

				<b-tab title="Log">
					<b-row class="mt-3">
						<b-col cols="12">
							<b-link
								class="float-right mb-3"
								title="Add a note"
								v-b-modal.sharedModal
								@click.prevent="
									modalContent = 'note';
									modalTitle = 'Add note';
								"
							>
								<b-icon
									font-scale="1.5"
									icon="plus-square"
								></b-icon>
							</b-link>
						</b-col>
					</b-row>

					<b-form-fieldset>
						<b-table-simple bordered striped>
							<b-thead head-variant="light">
								<tr>
									<th>Date</th>
									<th>Person</th>
									<th>Change</th>
								</tr>
							</b-thead>

							<b-tbody>
								<tr
									v-for="(log, index) in logs"
									:key="index + 10"
								>
									<td>{{ formatDate(log.Added) }}</td>

									<td>{{ log.FullName }}</td>
									<td>{{ log.Note }}</td>
								</tr>
							</b-tbody>
						</b-table-simple>
					</b-form-fieldset>
				</b-tab>
			</b-tabs>
		</b-form>

		<b-modal
			id="sharedModal"
			ref="sharedModal"
			:title="modalTitle"
			hide-footer
		>
			<add-clocks
				v-if="modalContent == 'clocks'"
				@addClocks="addClocks"
				:contract="contract"
			/>

			<add-note
				v-if="modalContent == 'note'"
				@addNote="addNote"
				entityType="ServiceContract"
				:entityId="contract.Id"
			/>
		</b-modal>

		<confirm ref="confirm"></confirm>
	</b-container>
</template>

<script>
import InvoiceCodeService from "@/api-services/invoicecode.service";
import ContractService from "@/api-services/contract.service";
import LogService from "@/api-services/log.service";
import SaveOrEditButtons from "@/components/partials/save-or-edit-buttons";
import AddClocks from "@/components/partials/add-clocks-to-contract";
import AddNote from "@/components/partials/add-note";
import DocumentAsset from "@/components/partials/document-contract";
import Confirm from "@/components/partials/confirm";

import { dateFuncs } from "@/globs.js";

export default {
	name: "contractUpdate",

	components: {
		SaveOrEditButtons,
		AddClocks,
		AddNote,
		DocumentAsset,
		Confirm,
	},

	computed: {
		invoiceCodeName() {
			return this.invoiceCodes
				.filter((q) => q.Id == this.contract.InvoiceCodeId)
				.map(function (obj) {
					return obj.Code;
				})
				.join();
		},

		statusName() {
			return this.states
				.filter((q) => q.Id == this.contract.ContractStatusId)
				.map(function (obj) {
					return obj.Name;
				})
				.join();
		},
	},

	data() {
		return {
			contract: {
				Customer: {},
				ServiceContractFile: {},
				Status: {},
				InvoiceCode: {},
				Clocks: [],
			},

			logs: [],

			invoiceCodes: [],

			states: [
				{ Id: 1, Name: "Active" },
				{ Id: 2, Name: "Paused" },
				{ Id: 3, Name: "Ended" },
			],

			editing: {
				id: null,
				index: null,
			},

			modalContent: null,
			modalTitle: "Select",

			creating: false,
			detailsEditable: false,
		};
	},

	created() {
		this.fetchContract();
		this.fetchInvoiceCodes();
	},

	methods: {
		editSection(section) {
			this[section] = true;
		},

		saveSection(section) {
			this.quietSave(section);
		},

		removeFromArray(section, index, hold) {
			if (hold) {
				this.$refs.confirm
					.open(
						"Confirm action",
						"Are you sure you want to remove this item from " +
							section
					)
					.then((confirm) => {
						if (confirm) {
							this.removeFromArray(section, index);
						}
					});
			} else {
				this.$delete(this.contract[section], index);
				this.quietSave();
			}
		},
		fetchLogs() {
			LogService.get("ServiceContract", this.contract.Id).then(
				(response) => {
					this.logs = response.data;
				}
			);
		},

		fetchInvoiceCodes() {
			InvoiceCodeService.getAll().then((response) => {
				this.invoiceCodes = response.data;
			});
		},

		fetchContract() {
			ContractService.get(this.$router.currentRoute.params.id).then(
				(response) => {
					this.contract = response.data;

					// make sure we can read the date
					let contractSqlDate = this.contract.FirstService;
					this.contract.FirstService = dateFuncs.frontEndDateFormat(
						contractSqlDate
					);

					// get logs
					this.fetchLogs();
				}
			);
		},

		addClock(clock) {
			this.contract.Clocks.push({
				Id: clock.Id,
				Name: clock.Name,
				Address: clock.Address,
			});

			this.quietSave();

			this.$nextTick(() => {
				this.modalContent = null;
				this.$refs.sharedModal.hide();
			});
		},

		addNote() {
			this.fetchLogs();

			this.$nextTick(() => {
				this.modalContent = null;
				this.$refs.sharedModal.hide();
			});
		},

		addClocks(clocks) {
			clocks.forEach((clock, i) => {
				this.contract.Clocks.push(clock);
			});

			this.quietSave();

			this.$nextTick(() => {
				this.modalContent = null;
				this.$refs.sharedModal.hide();
			});
		},

		deleteContract() {
			this.$refs.confirm
				.open(
					"Delete",
					"Are you sure you want to delete this contract?"
				)
				.then((confirm) => {
					ContractService.delete(this.contract.Id)
						.then(() => {
							this.$refs.confirm
								.open(
									"Success",
									"Successfully deleted contract",
									true
								)
								.then((confirm) => {
									this.$router.push({ name: "contracts" });
								});
						})
						.catch((error) => {
							let modal_options = {
								text: "light",
								background: "danger",
							};

							this.$refs.confirm.open(
								"Error",
								error.response.data,
								true,
								modal_options
							);
						});
				});
		},

		formatDate(date) {
			return dateFuncs.humanDateFormat(date);
		},

		quietSave(section) {
			ContractService.update(this.contract.Id, this.contract)
				.then(() => {
					// only close editable sections on success
					if (section) this[section] = false;

					this.fetchLogs();
				})
				.catch((error) => {
					let modal_options = {
						text: "light",
						background: "danger",
					};

					this.$refs.confirm.open(
						"Error",
						error.response.data,
						true,
						modal_options
					);
				});
		},
	},
};
</script>



// WEBPACK FOOTER //
// src/components/contract/contract-update.vue